'use client';

import Select from '@/components/atoms/Select';
import useAuth from '@/contexts/AuthProvider';
import { generateProductTitle } from '@/services/ecommerce/utils/MetaGenerator';
import { addFilterParams, generateProductLink, getProductImage } from '@/services/ecommerce/utils/helper';
import get from 'lodash/get';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
export const calculatePercentage = (d_price, price) => {
	return !!parseFloat(price) && !!parseFloat(d_price) ? (100 - (d_price / price) * 100).toFixed(0) : 0;
};

const AlternativeBrands = ({ data, product, searchParams }) => {
	const { isB2B } = useAuth();
	const router = useRouter();
	const pathName = usePathname();
	const userType = isB2B ? 'b2b' : 'b2c';
	const originalPrice = get(product?.pv, `0.pv_${userType}_price`, 0);
	const orginalMultiplePrice = get(product?.pv, `0.pu_${userType}_base_unit_multiplier`, 1);
	const originalPricePerUnit = originalPrice / orginalMultiplePrice;
	const getPercentageType = (item, price) => {
		const percentage = calculatePercentage(price, originalPricePerUnit) as any;
		let percentageType = 'same price';
		let percentageColor = '#757575';
		if (get(item?.pv, '0.pv_allow_sales', 0) === 0) {
			percentageType = 'Out of Stock';
			percentageColor = '#FD6A6A';
		} else if (!price) {
			percentageType = 'price unavailable';
		} else if (!originalPricePerUnit) {
			percentageType = '';
		} else if (percentage >= 1) {
			percentageType = `Save ${percentage}%`;
			percentageColor = '#0E7673';
		} else if (percentage < 0) {
			percentageType = `${percentage * -1}% costlier`;
			percentageColor = '#FF6C33';
		}

		return { percentageType, percentageColor };
	};

	const sortSameGenericProducts = (value) => {
		const updatedParams = addFilterParams(searchParams, {
			sort: value
		});
		router.push(`${pathName}?${updatedParams}`, {
			scroll: false
		});
	};
	const replacedText = data.block
		?.replace('{brand_name}', product?.p_brand?.pb_name || '')
		.replace('{product_name}', product?.p_name || '');

	if (data?.block_data?.length === 0) {
		return null;
	}
	return (
		<div className='bg-white rounded-10 mt-10  rounded-10'>
			<div className='d-flex justify-space-between p-20 border-b1-grey300 items-center'>
				<div
					className='text-18 text-grey900 fw-500 d-flex items-center'
					style={{
						width: '55%'
					}}>
					<h2 className='ml-5 mb-0'>{replacedText}</h2>
				</div>
				<div className='d-flex items-center gap-20'>
					<span className='text-16 text-grey500'>Sort by: </span>{' '}
					<Select
						defaultValue={searchParams?.sort || 'relevance'}
						style={{
							width: '123px',
							height: '40px',
							display: 'flex',
							alignItems: 'center',
							border: '1px solid #0E7673',
							borderRadius: '6px'
						}}
						valueStyle={{
							color: '#0E7673',
							fontWeight: 700
						}}
						className='text-primary fw-500'
						options={[
							{ label: 'Relevance', value: 'relevance' },
							{ label: 'Price', value: 'price' }
						]}
						onChange={(value) => sortSameGenericProducts(value)}
					/>
				</div>
			</div>
			<div
				className='d-flex flex-column p-20 pb-0'
				style={{
					maxHeight: '100vh',
					overflowY: 'auto',
					overflowX: 'hidden'
				}}>
				{data?.block_data?.map((pdata, index) => {
					const multiplier = get(pdata?.pv, `0.pu_${userType}_base_unit_multiplier`, 1);
					const price = get(pdata?.pv, `0.pv_${userType}_price`, 0);
					const pricePerUnit = price / multiplier;
					const combine = getPercentageType(pdata, pricePerUnit);
					const item = {
						name: pdata.p_name,
						strength: pdata.p_strength,
						company: pdata.p_brand,
						image: getProductImage(pdata),
						price: pricePerUnit.toFixed(2),
						percentageType: combine.percentageType,
						percentageColor: combine.percentageColor,
						p_allow_sales: pdata.p_allow_sales,
						p_form: pdata.p_form,
						pv_stock_status: get(pdata?.pv, `0.pv_stock_status`, 0),
						productCountOrdered: pdata.productCountOrdered,
						link: generateProductLink(pdata, get(pdata.pv, '0', {}))
					};
					return (
						<div key={index}>
							<Link
								className='d-flex justify-space-between mb-20 hover-text-primary cursor-pointer'
								style={{
									textDecoration: 'none'
								}}
								href={item?.link || '#'}>
								<div className='d-flex justify-center '>
									<div
										className='mr-20'
										style={{
											height: 46,
											width: 46,
											borderRadius: 6,
											cursor: 'pointer'
										}}>
										<Image
											src={item.image}
											alt={generateProductTitle({
												p_name: item.name,
												p_strength: item.strength,
												p_form: item.p_form
											})}
											width={46}
											height={46}
											quality={100}
											unoptimized={true}
											style={{
												objectFit: 'cover',
												borderRadius: 6,
												border: '1px solid #f5f5f5'
											}}
										/>
									</div>
									<div className='d-flex flex-column justify-space-between'>
										<div
											style={{
												display: 'flex',
												marginRight: '10px',
												lineHeight: '22px'
											}}>
											<span
												style={{
													marginRight: '10px',
													marginTop: '2px'
												}}>
												{item.pv_stock_status ? (
													<svg
														xmlns='http://www.w3.org/2000/svg'
														width='19'
														height='19'
														viewBox='0 0 19 19'
														fill='none'>
														<rect width='19' height='19' rx='9.5' fill='#323B49' />
														<mask
															id='mask0_218_21326'
															style={{ maskType: 'alpha' }}
															maskUnits='userSpaceOnUse'
															x='3'
															y='3'
															width='13'
															height='13'>
															<rect x='3' y='3' width='13' height='13' fill='#D9D9D9' />
														</mask>
														<g mask='url(#mask0_218_21326)'>
															<path
																d='M4.32767 8.72771L6.60267 6.45271C6.72906 6.32632 6.87802 6.23604 7.04954 6.18187C7.22107 6.12771 7.39711 6.11868 7.57767 6.15479L8.28184 6.30375C7.79434 6.88153 7.41066 7.40514 7.13079 7.87458C6.85093 8.34403 6.5801 8.91278 6.31829 9.58083L4.32767 8.72771ZM7.10371 9.96C7.31135 9.31 7.59347 8.69611 7.95007 8.11833C8.30666 7.54055 8.73774 6.99889 9.24329 6.49333C10.0377 5.69889 10.945 5.10531 11.9652 4.7126C12.9853 4.31989 13.9377 4.20028 14.8225 4.35375C14.9759 5.23847 14.8586 6.1909 14.4704 7.21104C14.0822 8.23118 13.4909 9.13847 12.6964 9.93292C12.1999 10.4294 11.6582 10.8605 11.0714 11.2261C10.4846 11.5918 9.86621 11.8784 9.21621 12.086L7.10371 9.96ZM10.8412 8.335C11.0489 8.54264 11.3039 8.64646 11.6063 8.64646C11.9087 8.64646 12.1638 8.54264 12.3714 8.335C12.5791 8.12736 12.6829 7.87233 12.6829 7.5699C12.6829 7.26746 12.5791 7.01243 12.3714 6.80479C12.1638 6.59715 11.9087 6.49333 11.6063 6.49333C11.3039 6.49333 11.0489 6.59715 10.8412 6.80479C10.6336 7.01243 10.5298 7.26746 10.5298 7.5699C10.5298 7.87233 10.6336 8.12736 10.8412 8.335ZM10.462 14.8485L9.59538 12.8579C10.2634 12.5961 10.8344 12.3253 11.3084 12.0454C11.7824 11.7656 12.3082 11.3819 12.886 10.8944L13.0214 11.5985C13.0575 11.7791 13.0485 11.9574 12.9943 12.1334C12.9402 12.3095 12.8499 12.4607 12.7235 12.5871L10.462 14.8485ZM5.19434 11.6933C5.51031 11.3774 5.89399 11.2171 6.34538 11.2126C6.79677 11.2081 7.18045 11.3638 7.49642 11.6798C7.81239 11.9958 7.97038 12.3794 7.97038 12.8308C7.97038 13.2822 7.81239 13.6659 7.49642 13.9819C7.27073 14.2076 6.89382 14.4017 6.36569 14.5642C5.83757 14.7267 5.10857 14.8711 4.17871 14.9975C4.3051 14.0676 4.44954 13.3409 4.61204 12.8173C4.77454 12.2937 4.96864 11.919 5.19434 11.6933Z'
																fill='#FFD023'
															/>
														</g>
													</svg>
												) : (
													<svg
														xmlns='http://www.w3.org/2000/svg'
														width='19'
														height='19'
														viewBox='0 0 19 19'
														fill='none'>
														<rect width='19' height='19' rx='9.5' fill='#0E7673' />
														<mask
															id='mask0_218_21305'
															style={{ maskType: 'alpha' }}
															maskUnits='userSpaceOnUse'
															x='4'
															y='4'
															width='11'
															height='11'>
															<rect x='4' y='4' width='11' height='11' fill='#D9D9D9' />
														</mask>
														<g mask='url(#mask0_218_21305)'>
															<path
																d='M11.0932 6.55443C10.8258 6.55443 10.6005 6.46276 10.4171 6.27943C10.2338 6.09609 10.1421 5.87075 10.1421 5.60339C10.1421 5.33602 10.2338 5.11068 10.4171 4.92734C10.6005 4.74401 10.8258 4.65234 11.0932 4.65234C11.3605 4.65234 11.5859 4.74401 11.7692 4.92734C11.9525 5.11068 12.0442 5.33602 12.0442 5.60339C12.0442 5.87075 11.9525 6.09609 11.7692 6.27943C11.5859 6.46276 11.3605 6.55443 11.0932 6.55443ZM8.99629 8.81172L10.0046 9.84297V12.7419H8.99629V10.4388L7.55254 9.15547C7.46087 9.07144 7.3883 8.97786 7.33483 8.87474C7.28136 8.77162 7.25462 8.64366 7.25462 8.49089C7.25462 8.36102 7.27754 8.24262 7.32337 8.13568C7.36921 8.02873 7.44559 7.92179 7.55254 7.81484L8.83587 6.53151C8.9199 6.44748 9.01348 6.38064 9.1166 6.33099C9.21973 6.28134 9.34768 6.25651 9.50046 6.25651C9.65323 6.25651 9.79073 6.27561 9.91296 6.3138C10.0352 6.352 10.1498 6.42457 10.2567 6.53151L11.1275 7.40234C11.3338 7.60859 11.5744 7.77092 11.8494 7.88932C12.1244 8.00773 12.4338 8.06693 12.7775 8.06693V9.07526C12.2734 9.07526 11.8284 8.9855 11.4426 8.80599C11.0569 8.62648 10.7074 8.39158 10.3942 8.1013L10.039 7.76901L8.99629 8.81172ZM6.37233 9.56797C6.99108 9.56797 7.51625 9.78377 7.94785 10.2154C8.37945 10.647 8.59525 11.1721 8.59525 11.7909C8.59525 12.4096 8.37945 12.9348 7.94785 13.3664C7.51625 13.798 6.99108 14.0138 6.37233 14.0138C5.75358 14.0138 5.22841 13.798 4.79681 13.3664C4.36521 12.9348 4.14941 12.4096 4.14941 11.7909C4.14941 11.1721 4.36521 10.647 4.79681 10.2154C5.22841 9.78377 5.75358 9.56797 6.37233 9.56797ZM6.37233 13.0628C6.72372 13.0628 7.02355 12.9386 7.27181 12.6904C7.52007 12.4421 7.64421 12.1423 7.64421 11.7909C7.64421 11.4395 7.52007 11.1397 7.27181 10.8914C7.02355 10.6431 6.72372 10.519 6.37233 10.519C6.02094 10.519 5.72112 10.6431 5.47285 10.8914C5.22459 11.1397 5.10046 11.4395 5.10046 11.7909C5.10046 12.1423 5.22459 12.4421 5.47285 12.6904C5.72112 12.9386 6.02094 13.0628 6.37233 13.0628ZM12.6286 9.56797C13.2473 9.56797 13.7725 9.78377 14.2041 10.2154C14.6357 10.647 14.8515 11.1721 14.8515 11.7909C14.8515 12.4096 14.6357 12.9348 14.2041 13.3664C13.7725 13.798 13.2473 14.0138 12.6286 14.0138C12.0098 14.0138 11.4847 13.798 11.0531 13.3664C10.6215 12.9348 10.4057 12.4096 10.4057 11.7909C10.4057 11.1721 10.6215 10.647 11.0531 10.2154C11.4847 9.78377 12.0098 9.56797 12.6286 9.56797ZM12.6286 13.0628C12.98 13.0628 13.2798 12.9386 13.5281 12.6904C13.7763 12.4421 13.9005 12.1423 13.9005 11.7909C13.9005 11.4395 13.7763 11.1397 13.5281 10.8914C13.2798 10.6431 12.98 10.519 12.6286 10.519C12.2772 10.519 11.9774 10.6431 11.7291 10.8914C11.4808 11.1397 11.3567 11.4395 11.3567 11.7909C11.3567 12.1423 11.4808 12.4421 11.7291 12.6904C11.9774 12.9386 12.2772 13.0628 12.6286 13.0628Z'
																fill='white'
															/>
														</g>
													</svg>
												)}
											</span>
											<span
												style={{
													// i want ...
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													maxWidth: '290px'
												}}>
												<span className='fw-500 text-grey900' style={{}}>
													{item.name}
												</span>

												{/* <span className="text-grey500 ">
													{!isEmpty(item?.p_form) && (
														<span className="ml-10 text-14 mb-10 text-grey600 text-capitalize">
															{item.p_form} -
															<span>({item.strength})</span>
														</span>
													)}
												</span> */}
											</span>
										</div>
										<div className='text-14 text-grey600 line-height-22'>By {item.company || 'Unknown'}</div>
									</div>
								</div>
								<div style={{ textAlign: 'right' }}>
									<div className='text-16 fw-600 text-grey900'>
										৳{item.price}/{item.p_form}
									</div>
									<div
										className={'text-14'}
										style={{
											color: item.percentageColor,
											lineHeight: '22px'
										}}>
										{item.percentageType}
									</div>
								</div>
							</Link>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default AlternativeBrands;
